import { ApiService } from '~/services/api/api'
import type { TestGradeRanking } from '~/types/GradeRanking'
import type { TestScoreDistribution } from '~/types/ScoreDistribution'
import type { PaginatedResponse } from '~/types/PaginatedResponse'
import type {
  TestSubmissionSummary,
  TestSubmissionsPaginationMeta,
} from '~/types/Submission'
import type {
  GenerateCategoryFeedbackRequest,
  GenerateSummaryFeedbackRequest,
  TestAttemptGrade,
  TestAttemptGradeEdits,
  TestAttemptGradingCategoryGrade,
} from '~/types/TestAttempt'

export class TestSubmissionsService {
  private readonly apiService = new ApiService()

  async getTestSubmissionSummariesByTestId(testId: string) {
    const response = await this.apiService.get<TestSubmissionSummary[]>(
      `/tests/${testId}/attempt-summaries`,
    )
    return response
  }

  async getTestSubmissionSummariesPaginated(
    testId: string,
    params: Record<string, string | number | undefined>,
  ) {
    const response = await this.apiService.get<
      PaginatedResponse<TestSubmissionSummary, TestSubmissionsPaginationMeta>
    >(`/tests/${testId}/attempt-summaries/paginated`, { params })
    return response
  }

  async getTestSubmissionSummary(testId: string, testAttemptId: string) {
    const response = await this.apiService.get<TestSubmissionSummary>(
      `/tests/${testId}/attempt-summaries/${testAttemptId}`,
    )
    return response
  }

  async getTestGradeRanking(testId: string) {
    const response = await this.apiService.get<TestGradeRanking>(
      `/tests/${testId}/grade-rankings`,
    )
    return response
  }

  async refreshTestGradeRanking(testId: string) {
    const response = await this.apiService.post<TestGradeRanking, undefined>(
      `/tests/${testId}/grade-rankings/refresh`,
      undefined,
    )
    return response
  }

  async getTestScoreDistribution(testId: string) {
    const response = await this.apiService.get<TestScoreDistribution>(
      `/tests/${testId}/score-distributions`,
    )
    return response
  }

  async refreshTestScoreDistribution(testId: string) {
    const response = await this.apiService.post<
      TestScoreDistribution,
      undefined
    >(`/tests/${testId}/score-distributions/refresh`, undefined)
    return response
  }

  async patchTestAttemptGradeEdits(
    testId: string,
    testAttemptId: string,
    testAttemptGradeId: string,
    edits: TestAttemptGradeEdits,
  ) {
    const response = await this.apiService.patch<
      TestAttemptGrade,
      TestAttemptGradeEdits
    >(
      `/tests/${testId}/attempts/${testAttemptId}/grades/${testAttemptGradeId}`,
      edits,
    )
    return response
  }

  async postGenerateCategoryFeedback(
    testId: string,
    testAttemptId: string,
    testAttemptGradeId: string,
    request: GenerateCategoryFeedbackRequest,
  ) {
    const response = await this.apiService.post<
      { feedback: string },
      GenerateCategoryFeedbackRequest
    >(
      `/tests/${testId}/attempts/${testAttemptId}/grades/${testAttemptGradeId}/category-feedback`,
      request,
    )
    return response
  }

  async postGenerateSummaryFeedback(
    testId: string,
    testAttemptId: string,
    testAttemptGradeId: string,
    request: GenerateSummaryFeedbackRequest,
  ) {
    const response = await this.apiService.post<
      { summaryFeedback: string },
      GenerateSummaryFeedbackRequest
    >(
      `/tests/${testId}/attempts/${testAttemptId}/grades/${testAttemptGradeId}/summary-feedback`,
      request,
    )
    return response
  }

  async overrideTestAttemptGradingCategoryGrade(
    testId: string,
    testAttemptId: string,
    testAttemptGradeId: string,
    testAttemptGradingCategoryGradeId: string,
    overrideLevel: number,
    overrideRationale: string,
  ) {
    const response =
      await this.apiService.patch<TestAttemptGradingCategoryGrade>(
        `/tests/${testId}/attempts/${testAttemptId}/grades/${testAttemptGradeId}/grading-category-grades/${testAttemptGradingCategoryGradeId}`,
        {
          overrideLevel,
          overrideRationale,
        },
      )
    return response
  }

  async overrideQuestionAttemptCategoryGrade(
    questionId: string,
    questionAttemptId: string,
    questionAttemptGradeId: string,
    questionAttemptGradingCategoryGradeId: string,
    overrideLevel: number,
    overrideRationale: string,
  ) {
    const response =
      await this.apiService.patch<TestAttemptGradingCategoryGrade>(
        `/questions/${questionId}/attempts/${questionAttemptId}/grades/${questionAttemptGradeId}/grading-category-grades/${questionAttemptGradingCategoryGradeId}`,
        {
          overrideLevel,
          overrideRationale,
        },
      )
    return response
  }

  async revertTestAttemptGradingCategoryGradeOverride(
    testId: string,
    testAttemptId: string,
    testAttemptGradeId: string,
    testAttemptGradingCategoryGradeId: string,
  ) {
    const response =
      await this.apiService.patch<TestAttemptGradingCategoryGrade>(
        `/tests/${testId}/attempts/${testAttemptId}/grades/${testAttemptGradeId}/grading-category-grades/${testAttemptGradingCategoryGradeId}`,
        {
          overrideLevel: null,
          overrideRationale: null,
          overrideLevelTitle: null,
          overrideLevelDescription: null,
          overrideScore: null,
        },
      )
    return response
  }

  async revertQuestionAttemptCategoryGradeOverride(
    questionId: string,
    questionAttemptId: string,
    questionAttemptGradeId: string,
    questionAttemptGradingCategoryGradeId: string,
  ) {
    const response =
      await this.apiService.patch<TestAttemptGradingCategoryGrade>(
        `/questions/${questionId}/attempts/${questionAttemptId}/grades/${questionAttemptGradeId}/grading-category-grades/${questionAttemptGradingCategoryGradeId}`,
        {
          overrideLevel: null,
          overrideRationale: null,
          overrideLevelTitle: null,
          overrideLevelDescription: null,
          overrideScore: null,
        },
      )
    return response
  }
}
